import {
  createContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useAuth0, User } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import { SupabaseUserInfo, UserRole } from "@/types/supabase";
import { formatUserName } from "@/lib/utils";
import { fetchWithAuth } from "@/lib/api";

// Define user status
export type UserStatus = "active" | "inactive" | "not_registered";

interface AuthContextType {
  // Auth0 properties
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;

  // App properties
  userNickname: string | null;
  supabaseInfo: SupabaseUserInfo | null;
  userRole: UserRole | null;
  userStatus: UserStatus | null;
  isDriver: boolean;
  isManager: boolean;
  isAdmin: boolean;
  userDataLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  setUserNickname: (nickname: string) => void;
  updateUserData: (updates: Partial<SupabaseUserInfo>) => void;
  refreshUserData: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const {
    isAuthenticated,
    isLoading,
    user,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
  } = useAuth0();
  const navigate = useNavigate();
  const [supabaseInfo, setSupabaseInfo] = useState<SupabaseUserInfo | null>(
    null,
  );
  const [userNickname, setUserNickname] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userStatus, setUserStatus] = useState<UserStatus | null>(null);
  const [userDataLoading, setUserDataLoading] = useState(false);
  // Provide default values during initial loading (memoized to prevent unnecessary re-renders)
  const defaultAuthState = useMemo(
    () => ({
      isAuthenticated: false,
      isLoading: true,
      user: null,
      userNickname: null,
      supabaseInfo: null,
      userRole: null,
      userStatus: null,
      isDriver: false,
      isManager: false,
      isAdmin: false,
      userDataLoading: false,
      login: async () => {},
      logout: async () => {},
      getAccessToken: async () => "",
      setUserNickname: () => {},
      updateUserData: () => {},
      refreshUserData: async () => {},
    }),
    [],
  );

  const validateUser = useCallback(async () => {
    if (isLoading) return;

    if (!isAuthenticated || !user?.email) {
      if (window.location.pathname !== "/terms") {
        navigate("/welcome", { replace: true });
      }
      return;
    }

    try {
      setUserDataLoading(true);

      const fetchUserResult = async (email: string) => {
        const token = await getAccessToken();
        const response = await fetchWithAuth(
          `/auth/users/email/${email}`,
          token,
        );
        return response;
      };
      const userResult = await fetchUserResult(user.email);
      const customer = userResult.user;
      const driver = userResult.driver;
      if (driver) {
        const driverName = formatUserName(driver.first_name, driver.last_name);
        if (driverName) {
          setUserNickname(driverName);
        }
        // Check if driver is active
        if (driver.active === false) {
          setUserStatus("inactive");
          navigate("/user-deactivated", { replace: true });
          return;
        }

        // User is a driver
        setUserRole("driver");
        setUserStatus("active");
        setSupabaseInfo(driver);

        // Redirect drivers to a specific route (e.g., driver dashboard)
        if (
          window.location.pathname === "/" ||
          window.location.pathname.startsWith("/dashboard")
        ) {
          navigate("/driver-portal", { replace: true });
        }
      } else if (customer) {
        const customerName = formatUserName(
          customer.first_name,
          customer.last_name,
        );
        if (customerName) {
          setUserNickname(customerName);
        }
        // Check if customer is active (assuming there's an active field)
        if (customer.active === false) {
          setUserStatus("inactive");
          navigate("/user-deactivated", { replace: true });
          return;
        }

        // User is a customer/admin - check their role
        const roleMapping: Record<string, UserRole> = {
          admin: "admin",
          manager: "manager",
        };

        const userRole = roleMapping[customer.role?.toLowerCase()] || "manager";

        setUserRole(userRole);
        setUserStatus("active");
        setSupabaseInfo({
          ...customer,
          role: customer.role,
          status: customer.active === false ? "inactive" : "active",
        });

        // Allow access to main app
        if (window.location.pathname === "/driver-portal") {
          navigate("/", { replace: true });
        }
      } else {
        // User not found in either table
        console.warn("User not found in database");
        setUserStatus("not_registered");
        navigate("/not-registered", { replace: true });
        return;
      }
    } catch (error) {
      console.error("Error validating user:", error);
      // If already authenticated at Auth0 level, avoid sending to welcome
      if (isAuthenticated) {
        navigate("/", { replace: true });
      } else {
        navigate("/welcome", { replace: true });
      }
    } finally {
      setUserDataLoading(false);
    }
  }, [isAuthenticated, user, isLoading, navigate]);

  useEffect(() => {
    validateUser();
  }, [validateUser]);

  const login = useCallback(async () => {
    await loginWithRedirect({
      appState: { returnTo: window.location.pathname },
    });
  }, [loginWithRedirect]);

  const customLogout = useCallback(async () => {
    await logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    navigate("/welcome");
  }, [logout, navigate]);

  const getAccessToken = useCallback(async () => {
    return await getAccessTokenSilently();
  }, [getAccessTokenSilently]);

  const updateUserData = useCallback((updates: Partial<SupabaseUserInfo>) => {
    setSupabaseInfo((prev) => (prev ? { ...prev, ...updates } : null));
  }, []);

  const refreshUserData = useCallback(async () => {
    await validateUser();
  }, [validateUser]);

  // Separar valores estables que no cambian frecuentemente
  const stableAuthState = useMemo(
    () => ({
      login,
      logout: customLogout,
      getAccessToken,
      setUserNickname,
      updateUserData,
      refreshUserData,
    }),
    [
      login,
      customLogout,
      getAccessToken,
      setUserNickname,
      updateUserData,
      refreshUserData,
    ],
  );

  // Separar valores dinámicos
  const dynamicAuthState = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user: user || null,
      userNickname,
      supabaseInfo,
      userRole,
      userStatus,
      userDataLoading,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      userNickname,
      supabaseInfo,
      userRole,
      userStatus,
      userDataLoading,
    ],
  );

  // Separar valores calculados
  const roleFlags = useMemo(
    () => ({
      isDriver: userRole === "driver",
      isManager: userRole === "manager",
      isAdmin: userRole === "admin",
    }),
    [userRole],
  );

  const value: AuthContextType = useMemo(() => {
    if (isLoading && !isAuthenticated && !user) {
      return defaultAuthState;
    }

    return {
      ...dynamicAuthState,
      ...roleFlags,
      ...stableAuthState,
    };
  }, [dynamicAuthState, roleFlags, stableAuthState, defaultAuthState]);

  return (
    <AuthContext.Provider value={value}>
      {isLoading ? (
        <div className="flex justify-center items-center h-screen">
          <LoadingSpinner />
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
}
